import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/**
 * A hover hint that survives being inside a scroll container.
 *
 * Two earlier attempts failed in different ways, and both are worth remembering:
 *
 *  - The shared TooltipWrapper (react-bootstrap OverlayTrigger) measures its target
 *    through a cloned ref. Around these anchors that came back empty and the bubble was
 *    drawn at the top-left of the page, over the tab bar.
 *  - A purely CSS bubble positioned against a relative wrapper is clipped by any
 *    ancestor with `overflow: auto` — which is exactly what the sender dropdown's
 *    scrolling list is, so the hint was cut off at the row's edge.
 *
 * So: measured here, on hover, from the anchor's own rect, and rendered into the body
 * so no ancestor can clip it. The measurement is ours and happens at a moment the
 * element is definitely laid out, which is what the cloned-ref version could not
 * promise.
 */

interface Props {
  text: string;
  children: React.ReactNode;
  /** Prefer below the anchor — for a table header, where there is nothing above. */
  below?: boolean;
  /** Which edge the bubble lines up with when it would otherwise run off-screen. */
  align?: 'center' | 'left' | 'right';
}

const MAX_W = 260;
const GAP = 8;

const InfoTip: React.FC<Props> = ({ text, children, below, align = 'center' }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    const left = align === 'left' ? r.left
      : align === 'right' ? r.right - MAX_W
        : r.left + r.width / 2 - MAX_W / 2;

    setPos({
      top: below ? r.bottom + GAP : r.top - GAP,
      // Kept on screen whatever the anchor's position — a hint half off the viewport
      // is no more readable than one that is clipped.
      left: Math.max(8, Math.min(left, window.innerWidth - MAX_W - 8)),
    });
  }, [align, below]);

  return (
    <>
      <Anchor
        ref={anchorRef}
        onMouseEnter={place}
        onMouseLeave={() => setPos(null)}
        onFocus={place}
        onBlur={() => setPos(null)}
      >
        {children}
      </Anchor>

      {pos && createPortal(
        <Bubble
          role="tooltip"
          $below={below}
          style={{ top: pos.top, left: pos.left }}
        >
          {text}
        </Bubble>,
        document.body,
      )}
    </>
  );
};

export default InfoTip;

const Anchor = styled.span`
  display: inline-flex; align-items: center;
`;

const Bubble = styled.span<{ $below?: boolean }>`
  position: fixed; z-index: 10000002;
  /* Anchored by its bottom edge when above, so it grows upward away from the anchor. */
  ${p => (p.$below ? '' : 'transform: translateY(-100%);')}
  width: max-content; max-width: ${MAX_W}px;
  padding: 7px 10px; border-radius: 7px;
  background: #1e293b; color: #fff;
  font-family: NunitoSans; font-size: 11.5px; font-weight: 500;
  line-height: 1.45; text-align: left;
  /* Sentences, not labels — the app's other CSS tooltip is nowrap and runs off-screen. */
  white-space: normal;
  text-transform: none; letter-spacing: 0;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
  pointer-events: none;
`;
